import React from "react";
import {
  Calendar,
  Facebook,
  Images,
  Instagram,
  Link as LinkIcon,
  Linkedin,
  MapPin,
  MessageSquare,
  Phone,
  Star,
  Twitter,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { formatPhoneDisplay } from "../../lib/phone-formatter";
import { type BaseComponentProps } from "../../types/puck";
import { type JobsiteBusinessContext } from "../../types/runtime-config";

/**
 * The link-in-bio page: one centered column of big tappable actions, served
 * live as the built-in "links" form on the booking host (SP-310,
 * wiki/decisions/link-page-form.md) and edited in the form builder.
 *
 * Rows are declared by kind, not by URL. Each auto kind resolves its target
 * from live business metadata at render time, so an untouched page stays
 * current as the business's data changes — and a row whose data is missing
 * (no GBP placeId yet, no gallery page) renders nothing on the live site
 * rather than a dead link. Only `custom` rows carry their own URL.
 */

export const LINKS_PAGE_LINK_KINDS = [
  "call",
  "book",
  "quote",
  "review",
  "directions",
  "gallery",
  "custom",
] as const;

export type LinksPageLinkKind = (typeof LINKS_PAGE_LINK_KINDS)[number];

/** utm_source carried on cross-property links (the booking app), matched by a
 *  branch in packages/attribution's classifyChannel. Keep the value stable —
 *  it becomes a channel label in every analytics dataset that stores it. */
export const LINKS_PAGE_UTM_SOURCE = "links_page";

export interface LinksPageLink {
  id?: string;
  kind: LinksPageLinkKind;
  /** Blank means "use the kind's default label". */
  label?: string;
  /** Blank means "resolve automatically from business data" (auto kinds).
   *  Set, it wins for every kind — the owner's URL beats the derived one.
   *  Custom rows have no automatic fallback, so theirs is required. */
  url?: string;
}

export interface LinksPageProps extends BaseComponentProps {
  /** Baked at page creation from the site's uploaded/scraped logo; editable. */
  logoUrl?: string;
  /** Blank derives "{city}, {state}" when the business has one. */
  tagline?: string;
  links?: LinksPageLink[];
  /** Photo grid under the link rows. Each photo optionally links somewhere. */
  photos?: LinksPagePhoto[];
  showRating?: "yes" | "no";
  showSocials?: "yes" | "no";
  /** Resolved by the Puck config from puck.metadata — never set as data. */
  business?: JobsiteBusinessContext;
  /** Real page paths, present in the static build; absent in the editor. */
  pagePaths?: string[];
  /** Absolute jobsite origin, set when the page renders OFF the jobsite (the
   *  go.servicepro.co form). Internal rows (quote, gallery) resolve against
   *  it so they land on the site instead of 404ing on the serving host. */
  siteBaseUrl?: string;
  isEditing?: boolean;
}

export interface LinksPagePhoto {
  id?: string;
  imageUrl?: string;
  /** Short line under the photo. */
  caption?: string;
  /** Where tapping the photo goes; blank means the photo is just a photo. */
  url?: string;
}

export function withUtmSource(url: string, source: string): string {
  return `${url}${url.includes("?") ? "&" : "?"}utm_source=${source}`;
}

interface ResolvedLinkRow {
  key: string;
  kind: LinksPageLinkKind;
  href: string;
  label: string;
  /** Sent as data-track — must stay within the tracker's [a-z_]+ vocabulary. */
  action: string;
  external: boolean;
}

/** Why a row rendered nothing — surfaced only as an editor placeholder. */
interface MissingLinkRow {
  key: string;
  kind: LinksPageLinkKind;
  reason: string;
}

/**
 * Whether an internal page exists. The editor preview has no pagePaths, so it
 * optimistically shows the row there; the static build knows and prunes.
 */
function hasPage(pagePaths: string[] | undefined, path: string): boolean {
  if (!pagePaths) {
    return true;
  }
  return pagePaths.some((p) => p === path || p === `${path}/`);
}

/** Default button text per kind. Exported so page seeding (page-presets) and
 *  the component agree on the words the pro sees in their Label fields —
 *  labels are seeded as real values, not resolved invisibly at render, so the
 *  editor shows exactly what's on the button. The call row appends the live
 *  phone number at render, which is the one part that must stay current. */
export const LINKS_PAGE_DEFAULT_LABELS: Record<LinksPageLinkKind, string> = {
  call: "Call",
  book: "Service request",
  quote: "Get a quote",
  review: "Leave us a review",
  directions: "Get directions",
  gallery: "See our work",
  custom: "Link",
};

const ROW_ACTIONS: Record<LinksPageLinkKind, string> = {
  call: "call",
  book: "book",
  quote: "quote",
  review: "review",
  directions: "directions",
  gallery: "gallery",
  custom: "link",
};

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

export function resolveLinksPageRows(
  links: LinksPageLink[],
  business: JobsiteBusinessContext | undefined,
  pagePaths: string[] | undefined,
  siteBaseUrl?: string,
): { rows: ResolvedLinkRow[]; missing: MissingLinkRow[] } {
  const sitePath = (path: string) =>
    siteBaseUrl ? `${siteBaseUrl.replace(/\/+$/, "")}${path}` : path;
  const rows: ResolvedLinkRow[] = [];
  const missing: MissingLinkRow[] = [];

  links.forEach((link, index) => {
    const key = link.id || `${link.kind}-${index}`;
    const label = link.label?.trim();

    // An owner-set URL wins for every kind, skipping the automatic
    // resolution AND its data preconditions — they chose the destination.
    const override = link.url?.trim();
    if (override && link.kind !== "custom") {
      rows.push({
        key,
        kind: link.kind,
        href: override,
        label: label || LINKS_PAGE_DEFAULT_LABELS[link.kind],
        action: ROW_ACTIONS[link.kind],
        external: isExternalHref(override),
      });
      return;
    }

    switch (link.kind) {
      case "call": {
        const phone = business?.phone?.trim();
        if (!phone) {
          missing.push({ key, kind: link.kind, reason: "Appears once the business has a phone number." });
          return;
        }
        rows.push({
          key,
          kind: link.kind,
          href: `tel:${phone.replace(/\D/g, "")}`,
          // The number IS the destination, so it stays on the button whatever
          // the label says — and stays current as the tracking number swaps.
          label: `${label || LINKS_PAGE_DEFAULT_LABELS.call} ${formatPhoneDisplay(phone)}`,
          action: "call",
          external: false,
        });
        return;
      }
      case "book": {
        const bookingUrl = business?.bookingUrl?.trim();
        if (!bookingUrl || business?.bookingEnabled === false) {
          missing.push({ key, kind: link.kind, reason: "Appears while online booking is turned on." });
          return;
        }
        rows.push({
          key,
          kind: link.kind,
          href: withUtmSource(bookingUrl, LINKS_PAGE_UTM_SOURCE),
          label: label || LINKS_PAGE_DEFAULT_LABELS.book,
          action: "book",
          external: true,
        });
        return;
      }
      case "quote": {
        if (!hasPage(pagePaths, "/contact")) {
          missing.push({ key, kind: link.kind, reason: "Appears once the site has a contact page." });
          return;
        }
        rows.push({
          key,
          kind: link.kind,
          href: sitePath("/contact"),
          label: label || LINKS_PAGE_DEFAULT_LABELS.quote,
          action: "quote",
          external: false,
        });
        return;
      }
      case "review": {
        const placeId = business?.placeId?.trim();
        if (!placeId) {
          missing.push({ key, kind: link.kind, reason: "Appears once the Google Business Profile is connected." });
          return;
        }
        rows.push({
          key,
          kind: link.kind,
          href: `https://search.google.com/local/writereview?placeid=${placeId}`,
          label: label || LINKS_PAGE_DEFAULT_LABELS.review,
          action: "review",
          external: true,
        });
        return;
      }
      case "directions": {
        const destination = business?.formattedAddress?.trim();
        if (!destination) {
          missing.push({ key, kind: link.kind, reason: "Appears once the business has an address." });
          return;
        }
        const placeIdParam = business?.placeId
          ? `&destination_place_id=${business.placeId}`
          : "";
        rows.push({
          key,
          kind: link.kind,
          href: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}${placeIdParam}`,
          label: label || LINKS_PAGE_DEFAULT_LABELS.directions,
          action: "directions",
          external: true,
        });
        return;
      }
      case "gallery": {
        if (!hasPage(pagePaths, "/gallery")) {
          missing.push({ key, kind: link.kind, reason: "Appears once the site has a gallery page." });
          return;
        }
        rows.push({
          key,
          kind: link.kind,
          href: sitePath("/gallery"),
          label: label || LINKS_PAGE_DEFAULT_LABELS.gallery,
          action: "gallery",
          external: false,
        });
        return;
      }
      case "custom": {
        const url = link.url?.trim();
        if (!url) {
          missing.push({ key, kind: link.kind, reason: "Add a URL to show this link." });
          return;
        }
        rows.push({
          key,
          kind: link.kind,
          href: url,
          label: label || url.replace(/^https?:\/\//, ""),
          action: "link",
          external: true,
        });
        return;
      }
    }
  });

  return { rows, missing };
}

const ROW_ICONS: Record<LinksPageLinkKind, React.ComponentType<{ className?: string }>> = {
  call: Phone,
  book: Calendar,
  quote: MessageSquare,
  review: Star,
  directions: MapPin,
  gallery: Images,
  custom: LinkIcon,
};

const SOCIAL_NETWORKS = [
  { key: "facebookUrl", label: "Facebook", Icon: Facebook },
  { key: "instagramUrl", label: "Instagram", Icon: Instagram },
  { key: "linkedinUrl", label: "LinkedIn", Icon: Linkedin },
  { key: "twitterUrl", label: "X (Twitter)", Icon: Twitter },
] as const;

export const LinksPage: React.FC<LinksPageProps> = ({
  id,
  className,
  logoUrl,
  tagline,
  links = [],
  photos = [],
  showRating = "yes",
  showSocials = "yes",
  business,
  pagePaths,
  siteBaseUrl,
  isEditing,
}) => {
  const { rows, missing } = resolveLinksPageRows(links, business, pagePaths, siteBaseUrl);
  // In an editor surface a tap is a selection, not a navigation — a tel: or
  // booking href firing mid-edit would yank the pro out of their work.
  const guardEditorClick = isEditing
    ? (event: React.MouseEvent) => {
        event.preventDefault();
      }
    : undefined;
  const visiblePhotos = photos.filter((photo) => Boolean(photo.imageUrl?.trim()));
  const emptyPhotoCount = photos.length - visiblePhotos.length;

  const name = business?.name?.trim();
  // An explicit logo wins; otherwise the business's stored logo keeps the
  // default (never-edited) page from rendering a bare monogram.
  const resolvedLogoUrl = logoUrl?.trim() || business?.logoUrl?.trim() || "";
  const resolvedTagline =
    tagline?.trim() ||
    (business?.city && business?.state ? `${business.city}, ${business.state}` : "");

  const rating = business?.googleRating;
  const reviewCount = business?.googleReviewCount;
  const showRatingBadge = showRating === "yes" && typeof rating === "number" && rating > 0;

  const socials =
    showSocials === "yes"
      ? SOCIAL_NETWORKS.flatMap(({ key, label, Icon }) => {
          const value = business?.[key];
          if (typeof value !== "string" || !value.trim()) {
            return [];
          }
          return [{ key, label, Icon, href: value.trim() }];
        })
      : [];

  return (
    <section
      id={id}
      aria-label="Links"
      className={cn("jobsite:bg-background jobsite:text-foreground", className)}
    >
      <div className="jobsite:mx-auto jobsite:flex jobsite:w-full jobsite:max-w-md jobsite:flex-col jobsite:items-center jobsite:gap-6 jobsite:px-5 jobsite:py-12">
        <header className="jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-3 jobsite:text-center">
          {resolvedLogoUrl ? (
            <img
              src={resolvedLogoUrl}
              alt={name ? `${name} logo` : "Business logo"}
              className="jobsite:h-20 jobsite:w-20 jobsite:object-contain"
              style={{ borderRadius: "var(--jobsite-radius)" }}
            />
          ) : name ? (
            <div
              aria-hidden="true"
              className="jobsite:flex jobsite:h-20 jobsite:w-20 jobsite:items-center jobsite:justify-center jobsite:bg-primary jobsite:font-heading jobsite:text-3xl jobsite:font-bold jobsite:text-primary-foreground"
              style={{ borderRadius: "var(--jobsite-radius)" }}
            >
              {name.charAt(0)}
            </div>
          ) : null}
          {name && (
            <h1 className="jobsite:m-0 jobsite:font-heading jobsite:text-2xl jobsite:font-bold">
              {name}
            </h1>
          )}
          {resolvedTagline && (
            <p className="jobsite:m-0 jobsite:text-sm jobsite:text-muted-foreground">
              {resolvedTagline}
            </p>
          )}
          {showRatingBadge && (
            <p className="jobsite:m-0 jobsite:inline-flex jobsite:items-center jobsite:gap-1.5 jobsite:text-sm jobsite:font-medium">
              <Star
                aria-hidden="true"
                className="jobsite:h-4 jobsite:w-4 jobsite:fill-current jobsite:text-primary"
              />
              {rating.toFixed(1)}
              {typeof reviewCount === "number" && reviewCount > 0 && (
                <span className="jobsite:font-normal jobsite:text-muted-foreground">
                  · {reviewCount} Google reviews
                </span>
              )}
            </p>
          )}
        </header>

        <nav aria-label="Business links" className="jobsite:flex jobsite:w-full jobsite:flex-col jobsite:gap-3">
          {rows.map((row) => {
            const Icon = ROW_ICONS[row.kind];
            const solid = row.kind === "call";
            return (
              <a
                key={row.key}
                href={row.href}
                onClick={guardEditorClick}
                data-track={row.action}
                data-track-label="links-page"
                {...(row.external ? { target: "_blank", rel: "noopener" } : {})}
                className={cn(
                  "jobsite:flex jobsite:min-h-14 jobsite:w-full jobsite:items-center jobsite:justify-center jobsite:gap-3 jobsite:px-5 jobsite:py-3.5 jobsite:text-center jobsite:font-semibold jobsite:transition-opacity jobsite:hover:opacity-85",
                  solid
                    ? "jobsite:bg-primary jobsite:text-primary-foreground"
                    : "jobsite:border jobsite:border-border jobsite:bg-card jobsite:text-card-foreground",
                )}
                style={{ borderRadius: "var(--jobsite-radius)" }}
              >
                <Icon aria-hidden="true" className="jobsite:h-5 jobsite:w-5 jobsite:shrink-0" />
                {row.label}
              </a>
            );
          })}

          {isEditing &&
            missing.map((row) => (
              <div
                key={row.key}
                className="jobsite:flex jobsite:w-full jobsite:items-center jobsite:justify-center jobsite:border jobsite:border-dashed jobsite:border-border jobsite:bg-muted/40 jobsite:px-5 jobsite:py-3 jobsite:text-center jobsite:text-sm jobsite:text-muted-foreground"
                style={{ borderRadius: "var(--jobsite-radius)" }}
              >
                {row.reason} Or set a URL on this row to link it anywhere now.
              </div>
            ))}
        </nav>

        {visiblePhotos.length > 0 && (
          <div className="jobsite:grid jobsite:w-full jobsite:grid-cols-3 jobsite:gap-2">
            {visiblePhotos.map((photo, index) => {
              const href = photo.url?.trim();
              const caption = photo.caption?.trim();
              const image = (
                <img
                  src={photo.imageUrl}
                  alt={caption || "Photo"}
                  loading="lazy"
                  className="jobsite:aspect-square jobsite:w-full jobsite:object-cover"
                  style={{ borderRadius: "var(--jobsite-radius)" }}
                />
              );
              const body = caption ? (
                <figure className="jobsite:m-0 jobsite:flex jobsite:flex-col jobsite:gap-1">
                  {image}
                  <figcaption className="jobsite:truncate jobsite:text-center jobsite:text-xs jobsite:text-muted-foreground">
                    {caption}
                  </figcaption>
                </figure>
              ) : (
                image
              );
              const key = photo.id || `photo-${index}`;
              if (!href) {
                return <React.Fragment key={key}>{body}</React.Fragment>;
              }
              return (
                <a
                  key={key}
                  href={href}
                  onClick={guardEditorClick}
                  data-track="link"
                  data-track-label="links-page"
                  {...(isExternalHref(href) ? { target: "_blank", rel: "noopener" } : {})}
                  className="jobsite:block jobsite:transition-opacity jobsite:hover:opacity-85"
                >
                  {body}
                </a>
              );
            })}
          </div>
        )}

        {isEditing && emptyPhotoCount > 0 && (
          <div
            className="jobsite:flex jobsite:w-full jobsite:items-center jobsite:justify-center jobsite:border jobsite:border-dashed jobsite:border-border jobsite:bg-muted/40 jobsite:px-5 jobsite:py-3 jobsite:text-center jobsite:text-sm jobsite:text-muted-foreground"
            style={{ borderRadius: "var(--jobsite-radius)" }}
          >
            {emptyPhotoCount === 1
              ? "A photo row is waiting for its picture."
              : `${emptyPhotoCount} photo rows are waiting for their pictures.`}
          </div>
        )}

        {socials.length > 0 && (
          <nav aria-label="Social profiles" className="jobsite:flex jobsite:items-center jobsite:gap-3">
            {socials.map(({ key, label, Icon, href }) => (
              <a
                key={key}
                href={href}
                onClick={guardEditorClick}
                target="_blank"
                rel="noopener"
                aria-label={label}
                data-track="social"
                data-track-label="links-page"
                className="jobsite:flex jobsite:h-11 jobsite:w-11 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:border jobsite:border-border jobsite:bg-card jobsite:text-card-foreground jobsite:transition-opacity jobsite:hover:opacity-85"
              >
                <Icon className="jobsite:h-5 jobsite:w-5" />
              </a>
            ))}
          </nav>
        )}
      </div>
    </section>
  );
};
